import React, { useState } from 'react'
import { useNotifications } from '../contexts/NotificationContext'
import TestNotifications from './TestNotifications'

const NotificationCenter = () => {
  const { 
    notifications, 
    preferences, 
    setPreferences, 
    markAsRead, 
    markAllAsRead, 
    dismissNotification, 
    clearAll,
    unreadCount
  } = useNotifications()

  const [activeFilter, setActiveFilter] = useState('all')
  const [showSettings, setShowSettings] = useState(false)

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return 'fas fa-exclamation-triangle text-danger'
      case 'warning': return 'fas fa-exclamation-circle text-warning'
      case 'info': return 'fas fa-info-circle text-info'
      case 'success': return 'fas fa-check-circle text-success'
      default: return 'fas fa-bell text-primary'
    }
  }

  const getPriorityLabel = (priority) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1)
  }

  const filteredNotifications = notifications.filter(notification => {
    switch (activeFilter) {
      case 'unread': return !notification.read
      case 'critical': return notification.priority === 'critical'
      case 'stock': return notification.category === 'stockAlerts'
      case 'device': return notification.category === 'deviceOffline'
      default: return true
    }
  })

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60))
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Notification Center</h2>
              <p className="text-muted mb-0">
                Manage alerts and notifications for your devices
                {unreadCount > 0 && (
                  <span className="badge bg-danger ms-2">{unreadCount} unread</span>
                )}
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowSettings(!showSettings)}
              >
                <i className="fas fa-cog me-1"></i>
                Settings
              </button>
              {notifications.length > 0 && (
                <>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    <i className="fas fa-check-double me-1"></i>
                    Mark All Read
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={clearAll}
                  >
                    <i className="fas fa-trash me-1"></i>
                    Clear All
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-cog me-2"></i>
                  Notification Preferences
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="stockAlerts"
                        checked={preferences.stockAlerts}
                        onChange={(e) => handlePreferenceChange('stockAlerts', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="stockAlerts">
                        Stock Level Alerts
                      </label>
                    </div>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="deviceOffline"
                        checked={preferences.deviceOffline}
                        onChange={(e) => handlePreferenceChange('deviceOffline', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="deviceOffline">
                        Device Status Alerts
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="soundEnabled"
                        checked={preferences.soundEnabled}
                        onChange={(e) => handlePreferenceChange('soundEnabled', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="soundEnabled">
                        Sound Alerts
                      </label>
                    </div>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="criticalOnly"
                        checked={preferences.criticalOnly}
                        onChange={(e) => handlePreferenceChange('criticalOnly', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="criticalOnly">
                        Critical Alerts Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Buttons */}
          <div className="card mb-4">
            <div className="card-body py-2">
              <div className="btn-group" role="group">
                <button
                  className={`btn btn-sm ${activeFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All ({notifications.length})
                </button>
                <button
                  className={`btn btn-sm ${activeFilter === 'unread' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setActiveFilter('unread')}
                >
                  Unread ({unreadCount})
                </button>
                <button
                  className={`btn btn-sm ${activeFilter === 'critical' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setActiveFilter('critical')}
                >
                  Critical ({notifications.filter(n => n.priority === 'critical').length})
                </button>
                <button
                  className={`btn btn-sm ${activeFilter === 'stock' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setActiveFilter('stock')}
                >
                  Stock ({notifications.filter(n => n.category === 'stockAlerts').length})
                </button>
                <button
                  className={`btn btn-sm ${activeFilter === 'device' ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => setActiveFilter('device')}
                >
                  Device ({notifications.filter(n => n.category === 'deviceOffline').length})
                </button>
              </div>
            </div>
          </div>

          {/* Test Notifications - Show when no notifications exist */}
          {notifications.length === 0 && <TestNotifications />}

          {/* Notifications List */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="mb-0">
                {activeFilter === 'all' ? 'All Notifications' : 
                 activeFilter === 'unread' ? 'Unread Notifications' :
                 activeFilter === 'critical' ? 'Critical Notifications' :
                 activeFilter === 'stock' ? 'Stock Alerts' :
                 'Device Alerts'}
              </h6>
            </div>
            <div className="card-body p-0">
              {filteredNotifications.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-bell fa-3x mb-3 d-block opacity-50"></i>
                  <p>No notifications found</p>
                  {activeFilter !== 'all' && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setActiveFilter('all')}
                    >
                      View All Notifications
                    </button>
                  )}
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`list-group-item list-group-item-action ${!notification.read ? 'bg-light' : ''}`}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            <i className={getPriorityIcon(notification.priority) + ' me-2'}></i>
                            <h6 className="mb-0 me-2">{notification.title}</h6>
                            <span className={`badge badge-sm bg-${
                              notification.priority === 'critical' ? 'danger' :
                              notification.priority === 'warning' ? 'warning' :
                              notification.priority === 'success' ? 'success' : 'info'
                            }`}>
                              {getPriorityLabel(notification.priority)}
                            </span>
                            {!notification.read && (
                              <span className="badge bg-primary ms-2">New</span>
                            )}
                          </div>
                          <p className="mb-1">{notification.message}</p>
                          <div className="d-flex align-items-center text-muted">
                            <small>{formatTimestamp(notification.timestamp)}</small>
                            {notification.deviceId && (
                              <>
                                <span className="mx-2">•</span>
                                <small>Device: {notification.deviceId}</small>
                              </>
                            )}
                            {notification.stockPercentage !== undefined && (
                              <>
                                <span className="mx-2">•</span>
                                <small>Stock: {notification.stockPercentage}%</small>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-column gap-1">
                          {!notification.read && (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => dismissNotification(notification.id)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotificationCenter